import React, { useMemo } from "react";

import { Box } from "@chakra-ui/react";
import { ReactComponent as CopyIcon } from "assets/icons/copy.svg";
import { handleCopyToClipboard } from "utils";
import { maskRandomDigits } from "utils/general/mask-data";
import { Maybe } from "utils/general/maybe";

import { BusinessContentItem } from "./BusinessContentItem";
import { formatDateOfBirth } from "./BusinessDirectorsInformation";
import { ComplianceStatus } from "./ComplianceStatus";
import { jobStatusTextMap } from "./FaceVerification";
import { IBusinessContentItem, IBvnNinInformationProps } from "./types";

export const BvnNinInformation = (props: IBvnNinInformationProps) => {
  const { business, isContentWrapperBelow745, isContentWrapperBelow600 } =
    props;

  const handleCopyItem = async (item: string, itemLabel: string) => {
    await handleCopyToClipboard(
      item ?? "",
      `${itemLabel} Copied Successfully!`
    );
  };

  const bvnNinInformation: IBusinessContentItem[] = useMemo(
    () => [
      {
        title: "Bank Verification Number",
        content: (
          <Box className="flex items-center gap-2">
            <p>
              {maskRandomDigits(business?.business?.companyData?.bvn) || "N/A"}
            </p>
            <Maybe condition={!!business?.business?.companyData?.bvn}>
              <CopyIcon
                cursor="pointer"
                onClick={() =>
                  handleCopyItem(
                    business?.business?.companyData?.bvn || "",
                    "BVN"
                  )
                }
              />
            </Maybe>
          </Box>
        ),
      },
      {
        title: "Date of Birth",
        content: business?.business?.companyData?.dob
          ? formatDateOfBirth(business?.business?.companyData?.dob)
          : "N/A",
      },
      {
        title: "BVN Reference",
        content: (
          <Box className="flex items-center gap-2">
            <p>{business?.business?.companyData?.bvnReference || "N/A"}</p>
            <Maybe condition={!!business?.business?.companyData?.bvnReference}>
              <CopyIcon
                cursor="pointer"
                onClick={() =>
                  handleCopyItem(
                    business?.business?.companyData?.bvnReference || "",
                    "BVN Reference"
                  )
                }
              />
            </Maybe>
          </Box>
        ),
      },
      {
        title: "National Identification Number",
        content: (
          <Box className="flex items-center gap-2">
            <p>
              {maskRandomDigits(business?.business?.companyData?.nin) || "N/A"}
            </p>
            <Maybe condition={!!business?.business?.companyData?.nin}>
              <CopyIcon
                cursor="pointer"
                onClick={() =>
                  handleCopyItem(
                    business?.business?.companyData?.nin || "",
                    "NIN"
                  )
                }
              />
            </Maybe>
          </Box>
        ),
      },
      {
        title: "Job ID",
        content: (
          <Box className="flex items-center gap-2">
            <p>{business?.business?.companyData?.bvnJobId || "N/A"}</p>
            <Maybe condition={!!business?.business?.companyData?.bvnJobId}>
              <CopyIcon
                cursor="pointer"
                onClick={() =>
                  handleCopyItem(
                    business?.business?.companyData?.bvnJobId || "",
                    "Job ID"
                  )
                }
              />
            </Maybe>
          </Box>
        ),
      },
      {
        title: "Job Status",
        content: business?.business?.companyData?.bvnJobStatus ? (
          <Box className="flex items-center gap-2">
            <ComplianceStatus
              status={
                jobStatusTextMap[business?.business?.companyData?.bvnJobStatus]
                  .status
              }
              text={
                jobStatusTextMap[business?.business?.companyData?.bvnJobStatus]
                  .text
              }
            />
          </Box>
        ) : (
          <p>N/A</p>
        ),
      },
    ],
    [isContentWrapperBelow745, isContentWrapperBelow600, business]
  );

  return (
    <Box>
      <Box
        css={{
          display: "flex",
          flexWrap: "wrap",
          columnGap: "32px",
          rowGap: "16px",
          justifyContent: "space-between",
        }}
      >
        {bvnNinInformation.map(({ title, content }, index) => (
          <BusinessContentItem
            key={index}
            title={title}
            content={content}
            isContentWrapperBelow745={isContentWrapperBelow745}
            isContentWrapperBelow600={isContentWrapperBelow600}
          />
        ))}
      </Box>
    </Box>
  );
};
